import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Center,
    Checkbox,
    Container,
    Divider,
    Grid,
    Group,
    Indicator,
    Loader,
    Modal,
    NumberInput,
    Paper,
    SegmentedControl,
    Select,
    Stack,
    Switch,
    Text,
    TextInput,
    ThemeIcon,
    Title,
    Pagination,
} from "@mantine/core";
import { DateInput, DatePicker, DateTimePicker } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import CountUp from "react-countup";
import {
    IconAlertTriangle,
    IconClipboardList,
    IconMoodSmile,
    IconPlus,
    IconUserCheck,
} from "@tabler/icons-react";

import ShiftCard, { ShiftDto } from "../../../components/shifts/ShiftCard";
import ParticipantsModal, {
    ShiftParticipantsResponse,
} from "../../../components/shifts/ParticipantsModal";
import { parseYmdLocal, ymd } from "../../../utils/date";
import {
    getEvents,
    getShiftsWithCounts,
    getShiftParticipants,
    createShift,
    updateShift,
    deleteShift,
    getAllowedEventIds,
    type EventItem,
    type FunctionItem,
    type ShiftUpsertDto,
    getFunctions,
} from "../../../api/shiftsApi";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billede1.jpg";
import { UnstyledButton } from "@mantine/core";
import * as XLSX from "xlsx";

type DayStat = { total: number; open: number; full: number };

function KpiCard({
    label,
    value,
    icon,
    hint,
}: {
    label: string;
    value: number;
    icon: React.ReactNode;
    hint?: string;
}) {
    return (
        <Paper
            withBorder
            p="md"
            radius="md"
            style={{ transition: "transform .14s ease, box-shadow .14s ease" }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0px)")}
        >
            <Group justify="space-between" align="flex-start">
                <Stack gap={2}>
                    <Text c="dimmed" size="sm">
                        {label}
                    </Text>
                    <Title order={3}>
                        <CountUp end={value} duration={0.6} />
                    </Title>
                    {hint && (
                        <Text size="xs" c="dimmed">
                            {hint}
                        </Text>
                    )}
                </Stack>

                <ThemeIcon radius="md" variant="light" size="lg">
                    {icon}
                </ThemeIcon>
            </Group>
        </Paper>
    );
}

export default function ShiftsPage() {
    const apiUrl = process.env.REACT_APP_API_URL as string;

    const [events, setEvents] = useState<EventItem[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

    const [functions, setFunctions] = useState<FunctionItem[]>([]);
    const [shifts, setShifts] = useState<ShiftDto[]>([]);
    const [loading, setLoading] = useState(false);

    const [selectedFunctionId, setSelectedFunctionId] = useState<number | "all">("all");
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [q, setQ] = useState("");

    const [showLedig, setShowLedig] = useState(true);
    const [showDelvist, setShowDelvist] = useState(true);
    const [showFuldt, setShowFuldt] = useState(true);

    const [dayStats, setDayStats] = useState<Record<string, DayStat>>({});

    const [viewShiftId, setViewShiftId] = useState<number | null>(null);
    const availableDays = useMemo(() => {
        const keys = Object.keys(dayStats).sort();
        return keys.map(parseYmdLocal);
    }, [dayStats]);

    const [view, setView] = useState<"list" | "calendar">("list");

    const [viewOpen, setViewOpen] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);
    const [viewShiftName, setViewShiftName] = useState<string>("Tilmeldte");
    const [participants, setParticipants] =
        useState<ShiftParticipantsResponse | null>(null);

    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formFunctionId, setFormFunctionId] = useState<number | null>(null);
    const [shiftsName, setShiftsName] = useState("");
    const [startDateTime, setStartDateTime] = useState<Date | null>(null);
    const [endDateTime, setEndDateTime] = useState<Date | null>(null);
    const [maxParticipants, setMaxParticipants] = useState<number>(1);
    const [waitlist, setWaitlist] = useState(false);

    const [editShiftId, setEditShiftId] = useState<number | null>(null);
    const isEditMode = editShiftId != null;

    const [activePage, setActivePage] = useState(1);
    const ITEMS_PER_PAGE = 25;
    const [functionSearch, setFunctionSearch] = useState("");
    const [exporting, setExporting] = useState(false);
    const [endsOneHourAfterPlay, setEndsOneHourAfterPlay] = useState(false);

    function resetFormForCreate() {
        setEditShiftId(null);
        setShiftsName("");
        setMaxParticipants(1);
        setWaitlist(false);
        setFormFunctionId(functions[0]?.functionId ?? null);
        setStartDateTime(null);
        setEndDateTime(null);
        setEndsOneHourAfterPlay(false);
    }

    function fillFormFromShift(s: ShiftDto, suffix?: string) {
        setFormFunctionId(s.functionID);
        setShiftsName(`${s.shiftsName ?? ""}${suffix ?? ""}`);
        setStartDateTime(new Date(s.startTime));
        setEndDateTime(new Date(s.endTime));
        setMaxParticipants(s.maxParticipants ?? 1);
        setWaitlist(!!s.waitlist);
        setEndsOneHourAfterPlay(!!s.endsOneHourAfterPlay);
    }

    function getRoles(): string[] {
        const raw = sessionStorage.getItem("userRoles");
        if (!raw) return [];

        try {
            const roles = JSON.parse(raw);
            return Array.isArray(roles) ? roles : [];
        } catch {
            return raw.split(",").map((r) => r.trim()).filter(Boolean);
        }
    }

    async function refreshAllAfterParticipantChange() {
        await loadParticipants();
        await loadShifts();
    }

    async function loadParticipants(shiftId?: number) {
        const id = shiftId ?? viewShiftId;
        if (!id) return;

        setViewLoading(true);
        try {
            const data = await getShiftParticipants(apiUrl, id);
            setParticipants(data);
        } catch (e) {
            console.error(e);
            notifications.show({
                color: "red",
                title: "Fejl",
                message: "Kunne ikke hente tilmeldte",
            });
            setParticipants(null);
        } finally {
            setViewLoading(false);
        }
    }
    async function exportParticipantsToExcel(type: "confirmed" | "waitlist") {
        if (!selectedEventId || filteredShifts.length === 0) {
            notifications.show({
                color: "yellow",
                title: "Ingen data",
                message: "Der er ingen vagter at eksportere ud fra de valgte filtre.",
            });
            return;
        }
        setExporting(true);

        try {
            const allRows: any[] = [];

            for (const shift of filteredShifts) {
                const data = await getShiftParticipants(apiUrl, shift.shiftsID);

                const rows =
                    type === "confirmed"
                        ? Array.isArray(data?.confirmed)
                            ? data.confirmed
                            : Array.isArray((data as any)?.confirmed?.$values)
                                ? (data as any).confirmed.$values
                                : []
                        : Array.isArray(data?.waitlist)
                            ? data.waitlist
                            : Array.isArray((data as any)?.waitlist?.$values)
                                ? (data as any).waitlist.$values
                                : [];

                rows.forEach((person: any) => {
                    allRows.push({
                        Vagt: shift.shiftsName ?? "",
                        Funktion: shift.functionName ?? "",
                        Start: shift.startTime
                            ? new Date(shift.startTime).toLocaleString("da-DK")
                            : "",
                        Slut: shift.endTime
                            ? new Date(shift.endTime).toLocaleString("da-DK")
                            : "",
                        Fornavn: person.firstName ?? "",
                        Efternavn: person.lastName ?? "",
                        Navn: `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim(),
                        Email: person.email ?? "",
                        Age: person.age,
                        Telefon: person.phone ?? "",
                        Status:
                            person.status ?? (type === "confirmed" ? "Tilmeldt" : "Venteliste"),
                        TilmeldtDato: person.createdAt
                            ? new Date(person.createdAt).toLocaleString("da-DK")
                            : "",
                    });
                });
            }

            if (allRows.length === 0) {
                notifications.show({
                    color: "yellow",
                    title: "Ingen deltagere",
                    message:
                        type === "confirmed"
                            ? "Der blev ikke fundet tilmeldte på de valgte vagter."
                            : "Der blev ikke fundet personer på venteliste på de valgte vagter.",
                });
                return;
            }

            const worksheet = XLSX.utils.json_to_sheet(allRows);
            const workbook = XLSX.utils.book_new();

            const sheetName = type === "confirmed" ? "Tilmeldte" : "Venteliste";
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

            const filePrefix = type === "confirmed" ? "tilmeldte" : "venteliste";
            const fileName = `${filePrefix}_${selectedEventName || selectedEventId}_${new Date()
                .toISOString()
                .slice(0, 10)}.xlsx`;

            XLSX.writeFile(workbook, fileName);

            notifications.show({
                color: "green",
                title: "Eksporteret",
                message:
                    type === "confirmed"
                        ? `Excel blev dannet med ${allRows.length} tilmeldte.`
                        : `Excel blev dannet med ${allRows.length} på venteliste.`,
            });
        } catch (e) {
            console.error(e);
            notifications.show({
                color: "red",
                title: "Fejl",
                message:
                    type === "confirmed"
                        ? "Kunne ikke eksportere tilmeldte til Excel."
                        : "Kunne ikke eksportere venteliste til Excel.",
            });
        } finally {
            setExporting(false);
        }
    }
    function toLocalDateTimeString(date: Date) {
        const pad = (n: number) => String(n).padStart(2, "0");

        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    }

    function getUserId(): number | null {
        const raw = sessionStorage.getItem("userId");
        if (!raw) return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
    }

    useEffect(() => {
        (async () => {
            try {
                const roles = getRoles();
                const isAdmin = roles.includes("Admin");
                const isClub = roles.includes("Club");

                const allEvents = await getEvents(apiUrl);

                if (isAdmin || !isClub) {
                    setEvents(allEvents);
                    return;
                }

                const userId = getUserId();
                if (!userId) {
                    setEvents([]);
                    return;
                }

                const allowedIds = await getAllowedEventIds(apiUrl, userId);
                const allowedSet = new Set(allowedIds);

                const filtered = allEvents.filter((e) => allowedSet.has(e.eventID));
                setEvents(filtered);

                setSelectedEventId((prev) => (prev && allowedSet.has(prev) ? prev : null));
            } catch (e) {
                console.error(e);
                notifications.show({
                    color: "red",
                    title: "Fejl",
                    message: "Kunne ikke hente events",
                });
                setEvents([]);
            }
        })();
    }, [apiUrl]);

    useEffect(() => {
        if (!selectedEventId) {
            setFunctions([]);
            setShifts([]);
            setDayStats({});
            return;
        }

        (async () => {
            try {
                const data = await getFunctions(apiUrl, selectedEventId);
                setFunctions(data);
                setSelectedFunctionId("all");
                setSelectedDate(null);
            } catch (e) {
                console.error(e);
                notifications.show({
                    color: "red",
                    title: "Fejl",
                    message: "Kunne ikke hente funktioner",
                });
                setFunctions([]);
            }
        })();
    }, [apiUrl, selectedEventId]);

    useEffect(() => {
        if (!selectedEventId) return;

        (async () => {
            try {
                const data = await getShiftsWithCounts(apiUrl, selectedEventId, {
                    functionId:
                        selectedFunctionId === "all" ? undefined : selectedFunctionId,
                });

                const safeData = Array.isArray(data) ? data : (data as any)?.$values ?? [];

                const stats: Record<string, DayStat> = {};
                safeData.forEach((s: ShiftDto) => {
                    const dt = new Date(s.startTime);
                    const key = ymd(
                        new Date(dt.getFullYear(), dt.getMonth(), dt.getDate())
                    );
                    const isFull = (s.registeredCount ?? 0) >= s.maxParticipants;

                    if (!stats[key]) stats[key] = { total: 0, open: 0, full: 0 };
                    stats[key].total += 1;
                    if (isFull) stats[key].full += 1;
                    else stats[key].open += 1;
                });

                setDayStats(stats);
            } catch (e) {
                console.error(e);
                setDayStats({});
            }
        })();
    }, [apiUrl, selectedEventId, selectedFunctionId]);

    const readOnly = useMemo(() => {
        const raw = sessionStorage.getItem("userRoles");
        if (!raw) return true;

        try {
            const roles = JSON.parse(raw);
            if (Array.isArray(roles)) return !roles.includes("Admin");
        } catch {
            const roles = raw.split(",").map((r) => r.trim());
            return !roles.includes("Admin");
        }

        return true;
    }, []);

    async function loadShifts() {
        if (!selectedEventId) return;

        setLoading(true);
        try {
            const data = await getShiftsWithCounts(apiUrl, selectedEventId, {
                functionId:
                    selectedFunctionId === "all" ? undefined : selectedFunctionId,
                date: selectedDate ? ymd(selectedDate) : undefined,
            });
            const safeData = Array.isArray(data) ? data : (data as any)?.$values ?? [];
            setShifts(safeData);
        } catch (e) {
            console.error(e);
            notifications.show({
                color: "red",
                title: "Fejl",
                message: "Kunne ikke hente vagter",
            });
            setShifts([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!selectedEventId) return;
        loadShifts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEventId, selectedFunctionId, selectedDate]);

    useEffect(() => {
        setActivePage(1);
    }, [q, showLedig, showDelvist, showFuldt, selectedFunctionId, selectedDate, view, selectedEventId]);

    const eventSelectData = useMemo(() => {
        return events.map((e) => ({
            value: String(e.eventID),
            label: e.eventName || `Event ${e.eventID}`,
        }));
    }, [events]);

    const selectedEventName = useMemo(() => {
        const e = events.find((x) => x.eventID === selectedEventId);
        return e?.eventName ?? "";
    }, [events, selectedEventId]);

    const functionSelectData = useMemo(() => {
        const mapped = functions
            .filter((f) => f?.functionId != null && f.functionCategoryId === 10)
            .map((f) => ({
                value: String(f.functionId),
                label: f.functionName?.trim() || `Funktion ${f.functionId}`,
            }));

        return [{ value: "all", label: "Alle Funktioner" }, ...mapped];
    }, [functions]);

    const createFunctionSelectData = useMemo(() => {
        return functions
            .filter((f) => f?.functionId != null && f.functionCategoryId === 10)
            .map((f) => ({
                value: String(f.functionId),
                label: f.functionName?.trim() || `Funktion ${f.functionId}`,
            }));
    }, [functions]);

    const filteredShifts = useMemo(() => {
        const query = q.trim().toLowerCase();
        const safeShifts = Array.isArray(shifts) ? shifts : [];

        return safeShifts.filter((s) => {
            const filled = (s.registeredCount ?? 0) >= s.maxParticipants;
            const partial = (s.registeredCount ?? 0) > 0 && !filled;
            const empty = (s.registeredCount ?? 0) === 0;

            if (empty && !showLedig) return false;
            if (partial && !showDelvist) return false;
            if (filled && !showFuldt) return false;

            if (!query) return true;
            return `${s.shiftsName} ${s.functionName}`.toLowerCase().includes(query);
        });
    }, [shifts, q, showLedig, showDelvist, showFuldt]);

    const kpi = useMemo(() => {
        const safeShifts = Array.isArray(shifts) ? shifts : [];

        const excludedFunctionNames = ["efterskoler"];

        const includedShifts = safeShifts.filter((s) => {
            const functionName = (s.functionName ?? "").trim().toLowerCase();
            return !excludedFunctionNames.includes(functionName);
        });

        const total = safeShifts.length;

        const filled = includedShifts.filter(
            (s) => (s.registeredCount ?? 0) >= s.maxParticipants
        ).length;

        const openCount = includedShifts.length - filled;

        const registeredSum = safeShifts.reduce(
            (a, s) => a + (s.registeredCount ?? 0),
            0
        );

        const waitlistSum = safeShifts.reduce(
            (a, s) => a + (s.waitlistCount ?? 0),
            0
        );

        const missingSlots = includedShifts.reduce((a, s) => {
            const registered = s.registeredCount ?? 0;
            const max = s.maxParticipants ?? 0;
            return a + Math.max(0, max - registered);
        }, 0);

        return {
            total,
            openCount,
            registeredSum,
            waitlistSum,
            missingSlots,
        };
    }, [shifts]);

    const totalPages = Math.ceil(filteredShifts.length / ITEMS_PER_PAGE);

    const paginatedShifts = filteredShifts.slice(
        (activePage - 1) * ITEMS_PER_PAGE,
        activePage * ITEMS_PER_PAGE
    );

    function openCreateModal() {
        resetFormForCreate();
        setOpen(true);
    }

    function openEditShift(s: ShiftDto) {
        setEditShiftId(s.shiftsID);
        fillFormFromShift(s);
        setOpen(true);
    }

    function openCopyShift(s: ShiftDto) {
        setEditShiftId(null);
        fillFormFromShift(s, " (kopi)");
        setOpen(true);
    }

    function validateForm(): string | null {
        if (!selectedEventId) return "Vælg et event";
        if (!formFunctionId) return "Vælg en funktion";
        if (!shiftsName.trim()) return "Udfyld vagt navn";
        if (!startDateTime || !endDateTime) return "Vælg start og slut";
        if (endDateTime <= startDateTime) return "Slut skal være efter start";
        return null;
    }

    async function upsertShift() {
        const err = validateForm();
        if (err) {
            notifications.show({ color: "red", title: "Mangler", message: err });
            return;
        }

        const payload: ShiftUpsertDto = {
            functionId: formFunctionId!,
            shiftsName: shiftsName.trim(),
            startTime: toLocalDateTimeString(startDateTime!),
            endTime: toLocalDateTimeString(endDateTime!),
            maxParticipants,
            waitlist,
            ticketID: null,
            endsOneHourAfterPlay
        };

        setSaving(true);
        try {
            if (isEditMode) await updateShift(apiUrl, editShiftId!, payload);
            else await createShift(apiUrl, payload);

            notifications.show({
                title: isEditMode ? "Gemt" : "Oprettet",
                message: isEditMode ? "Vagten blev opdateret ✅" : "Vagten blev oprettet ✅",
            });

            setOpen(false);
            setEditShiftId(null);
            await loadShifts();
        } catch (e) {
            console.error(e);
            notifications.show({
                color: "red",
                title: "Fejl",
                message: "Kunne ikke gemme vagt",
            });
        } finally {
            setSaving(false);
        }
    }

    async function onDeleteShift(id: number) {
        if (!window.confirm("Slet vagten?")) return;

        try {
            await deleteShift(apiUrl, id);
            notifications.show({ title: "Slettet", message: "Vagten blev slettet" });
            await loadShifts();
        } catch (e) {
            console.error(e);
            notifications.show({
                color: "red",
                title: "Fejl",
                message: "Kunne ikke slette vagten",
            });
        }
    }

    async function openParticipants(shift: ShiftDto) {
        setViewShiftName(`Tilmeldte · ${shift.shiftsName}`);
        setViewShiftId(shift.shiftsID);
        setParticipants(null);
        setViewOpen(true);

        await loadParticipants(shift.shiftsID);
    }

    const selectedKey = selectedDate ? ymd(selectedDate) : "all";
    const ACCENT = "#e63946";

    return (
        <div>
            <HeroSectionV2
                titleEn="Shifts view"
                titleDa="Vagtview"
                descriptionEn=""
                descriptionDa=""
                imageSrc={Billede1}
                badgeText="Shifts"
            />

            <Container size="xl" py="md">
                <Stack gap="md">
                    <Paper withBorder p="lg" radius="md">
                        <Group justify="space-between" align="flex-end">
                            <Stack gap={2}>
                                <Title order={3}>
                                    {selectedEventName ? `Vagter · ${selectedEventName}` : "Vagt-overblik"}
                                </Title>
                                <Text c="dimmed" size="sm">
                                    Vælg et event og administrér vagter, funktioner og tilmeldinger
                                </Text>
                            </Stack>

                            <Select
                                label="Vælg event"
                                placeholder="Vælg event..."
                                data={eventSelectData}
                                value={selectedEventId ? String(selectedEventId) : null}
                                onChange={(v) => setSelectedEventId(v ? Number(v) : null)}
                                searchable
                                w={320}
                            />
                        </Group>
                    </Paper>

                    {!selectedEventId ? (
                        <Paper withBorder p="xl" radius="md">
                            <Text c="dimmed">Vælg et event øverst for at se KPI’er, filtre og vagter.</Text>
                        </Paper>
                    ) : (
                        <>
                            <Grid>
                                <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
                                    <KpiCard
                                        label="Total vagter"
                                        value={kpi.total}
                                        icon={<IconClipboardList size={18} />}
                                        hint="Opdateres automatisk"
                                    />
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
                                    <KpiCard
                                        label="Ledige vagter"
                                        value={kpi.openCount}
                                        icon={<IconMoodSmile size={18} />}
                                        hint="Har stadig pladser"
                                    />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                                        <KpiCard
                                            label="Ledige pladser i alt"
                                            value={kpi.missingSlots}
                                            icon={<IconPlus size={18} />}
                                            hint="Mangler at blive besat"
                                        />
                                    </Grid.Col>
                                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                                    <KpiCard
                                        label="Frivillige tilmeldt"
                                        value={kpi.registeredSum}
                                        icon={<IconUserCheck size={18} />}
                                        hint="Samlet tilmeldte"
                                    />
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
                                    <KpiCard
                                        label="Venteliste"
                                        value={kpi.waitlistSum}
                                        icon={<IconAlertTriangle size={18} />}
                                        hint="Samlet venteliste"
                                    />
                                </Grid.Col>
                            </Grid>

                            <Grid>
                                <Grid.Col span={{ base: 12, md: 3 }}>
                                    <Paper withBorder p="md" radius="md">
                                        <Stack gap="md">
                                                <Select
                                                    label="Funktion"
                                                    placeholder="Søg funktion..."
                                                    data={functionSelectData}
                                                    value={selectedFunctionId === "all" ? "all" : String(selectedFunctionId)}
                                                    onChange={(v) => {
                                                        if (!v || v === "all") setSelectedFunctionId("all");
                                                        else setSelectedFunctionId(Number(v));
                                                    }}
                                                    searchable
                                                    clearable
                                                    nothingFoundMessage="Ingen funktioner fundet"
                                                    maxDropdownHeight={250}
                                                    searchValue={functionSearch}
                                                    onSearchChange={setFunctionSearch}
                                                    onFocus={() => setFunctionSearch("")}
                                                />

                                            <DateInput
                                                label="Dato"
                                                value={selectedDate}
                                                onChange={setSelectedDate}
                                                clearable
                                            />

                                            <TextInput
                                                label="Søg"
                                                placeholder="Søg i vagter/funktioner…"
                                                value={q}
                                                onChange={(e) => setQ(e.currentTarget.value)}
                                            />

                                            <Divider />
                                            <Text fw={600} size="sm">
                                                Status
                                            </Text>
                                            <Checkbox
                                                label="Ledig"
                                                checked={showLedig}
                                                onChange={(e) => setShowLedig(e.currentTarget.checked)}
                                            />
                                            <Checkbox
                                                label="Delvist"
                                                checked={showDelvist}
                                                onChange={(e) => setShowDelvist(e.currentTarget.checked)}
                                            />
                                            <Checkbox
                                                label="Fuldt"
                                                checked={showFuldt}
                                                onChange={(e) => setShowFuldt(e.currentTarget.checked)}
                                            />
                                        </Stack>
                                    </Paper>
                                </Grid.Col>

                                <Grid.Col span={{ base: 12, md: 9 }}>
                                    <Stack gap="sm">
                                        <Group justify="space-between">
                                            <Title order={4}>Vagter</Title>

                                                <Group>
                                                    <SegmentedControl
                                                        value={view}
                                                        onChange={(v) => setView(v as "list" | "calendar")}
                                                        data={[
                                                            { label: "Liste", value: "list" },
                                                            { label: "Kalender", value: "calendar" },
                                                        ]}
                                                    />

                                                    <Button
                                                        variant="light"
                                                        loading={exporting}
                                                        onClick={() => exportParticipantsToExcel("confirmed")}
                                                    >
                                                        Eksportér tilmeldte
                                                    </Button>

                                                    <Button
                                                        variant="light"
                                                        loading={exporting}
                                                        onClick={() => exportParticipantsToExcel("waitlist")}
                                                    >
                                                        Eksportér venteliste
                                                    </Button>

                                                    {!readOnly && (
                                                        <Button leftSection={<IconPlus size={16} />} onClick={openCreateModal}>
                                                            Opret vagt
                                                        </Button>
                                                    )}
                                                </Group>
                                        </Group>

                                        <Paper withBorder radius="md" px="sm" pt={6} pb="md">
                                            <div
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "repeat(auto-fill, minmax(88px, 1fr))",
                                                    gap: 8,
                                                    alignItems: "start",
                                                }}
                                            >
                                                <UnstyledButton
                                                    onClick={() => setSelectedDate(null)}
                                                    style={{
                                                        height: 54,
                                                        borderRadius: 10,
                                                        border: "1px solid transparent",
                                                        background: selectedKey === "all" ? ACCENT : "#f1f3f5",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontWeight: 800,
                                                        color: selectedKey === "all" ? "white" : "#111",
                                                    }}
                                                >
                                                    Alle
                                                </UnstyledButton>

                                                {availableDays.map((d) => {
                                                    const key = ymd(d);
                                                    const isActive = selectedKey === key;

                                                    const weekday = d.toLocaleDateString("da-DK", { weekday: "short" });
                                                    const dateLabel = d.toLocaleDateString("da-DK", {
                                                        day: "2-digit",
                                                        month: "short",
                                                    });

                                                    return (
                                                        <UnstyledButton
                                                            key={key}
                                                            onClick={() => setSelectedDate(parseYmdLocal(key))}
                                                            style={{
                                                                height: 54,
                                                                borderRadius: 10,
                                                                border: "1px solid transparent",
                                                                background: isActive ? ACCENT : "#f1f3f5",
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                lineHeight: 1.05,
                                                                color: isActive ? "white" : "#111",
                                                            }}
                                                        >
                                                            <Text size="xs" fw={800} c={isActive ? "white" : undefined}>
                                                                {weekday}
                                                            </Text>
                                                            <Text
                                                                size="xs"
                                                                c={isActive ? "white" : "dimmed"}
                                                                style={{ opacity: isActive ? 0.9 : 1 }}
                                                            >
                                                                {dateLabel}
                                                            </Text>
                                                        </UnstyledButton>
                                                    );
                                                })}
                                            </div>
                                        </Paper>

                                        <Paper withBorder p="md" radius="md">
                                            {loading ? (
                                                <Center>
                                                    <Loader />
                                                </Center>
                                            ) : view === "calendar" ? (
                                                <Grid>
                                                    <Grid.Col span={{ base: 12, md: 4 }}>
                                                        <Paper withBorder p="md" radius="md">
                                                            <Group justify="space-between" mb="sm">
                                                                <Text fw={700}>Kalender</Text>
                                                                <Button variant="subtle" onClick={() => setSelectedDate(new Date())}>
                                                                    I dag
                                                                </Button>
                                                            </Group>

                                                            <DatePicker
                                                                value={selectedDate}
                                                                onChange={setSelectedDate}
                                                                firstDayOfWeek={1}
                                                                weekendDays={[6, 0]}
                                                                renderDay={(date) => {
                                                                    const day = date.getDate();
                                                                    const key = ymd(date);
                                                                    const stat = dayStats[key];

                                                                    if (!stat) return <div>{day}</div>;

                                                                    const color = stat.open > 0 ? "green" : "blue";
                                                                    return (
                                                                        <Indicator size={10} color={color} offset={8}>
                                                                            <div style={{ fontWeight: 800 }}>{day}</div>
                                                                        </Indicator>
                                                                    );
                                                                }}
                                                            />

                                                            <Text size="xs" c="dimmed" mt="sm">
                                                                Grøn = der er ledige vagter · Blå = alt fuldt
                                                            </Text>
                                                        </Paper>
                                                    </Grid.Col>

                                                    <Grid.Col span={{ base: 12, md: 8 }}>
                                                        <Paper withBorder p="md" radius="md">
                                                            <Group justify="space-between" align="center" mb="sm">
                                                                <Stack gap={2}>
                                                                    <Text fw={700}>Dagsagenda</Text>
                                                                    <Text size="sm" c="dimmed">
                                                                        {selectedDate
                                                                            ? selectedDate.toLocaleDateString("da-DK", {
                                                                                weekday: "long",
                                                                                day: "2-digit",
                                                                                month: "long",
                                                                                year: "numeric",
                                                                            })
                                                                            : "Alle dage"}
                                                                    </Text>
                                                                </Stack>

                                                                {!readOnly && (
                                                                    <Button leftSection={<IconPlus size={16} />} onClick={openCreateModal}>
                                                                        Opret vagt
                                                                    </Button>
                                                                )}
                                                            </Group>

                                                            {filteredShifts.length === 0 ? (
                                                                <Text c="dimmed">Ingen vagter matcher dine filtre.</Text>
                                                            ) : (
                                                                <>
                                                                    <Stack gap="sm">
                                                                        {paginatedShifts.map((s) => (
                                                                            <ShiftCard
                                                                                key={s.shiftsID}
                                                                                shift={s}
                                                                                onView={openParticipants}
                                                                                onDelete={readOnly ? undefined : onDeleteShift}
                                                                                onEdit={readOnly ? undefined : openEditShift}
                                                                                onCopy={readOnly ? undefined : openCopyShift}
                                                                            />
                                                                        ))}
                                                                    </Stack>

                                                                    {totalPages > 1 && (
                                                                        <Center mt="md">
                                                                            <Stack align="center" gap="xs">
                                                                                <Text size="sm" c="dimmed">
                                                                                    Viser {(activePage - 1) * ITEMS_PER_PAGE + 1}-
                                                                                    {Math.min(
                                                                                        activePage * ITEMS_PER_PAGE,
                                                                                        filteredShifts.length
                                                                                    )}{" "}
                                                                                    af {filteredShifts.length} vagter
                                                                                </Text>
                                                                                <Pagination
                                                                                    value={activePage}
                                                                                    onChange={setActivePage}
                                                                                    total={totalPages}
                                                                                />
                                                                            </Stack>
                                                                        </Center>
                                                                    )}
                                                                </>
                                                            )}
                                                        </Paper>
                                                    </Grid.Col>
                                                </Grid>
                                            ) : filteredShifts.length === 0 ? (
                                                <Text c="dimmed">Ingen vagter matcher dine filtre.</Text>
                                            ) : (
                                                <>
                                                    <Stack gap="sm">
                                                        {paginatedShifts.map((s) => (
                                                            <ShiftCard
                                                                key={s.shiftsID}
                                                                shift={s}
                                                                onView={openParticipants}
                                                                onDelete={readOnly ? undefined : onDeleteShift}
                                                                onEdit={readOnly ? undefined : openEditShift}
                                                                onCopy={readOnly ? undefined : openCopyShift}
                                                            />
                                                        ))}
                                                    </Stack>

                                                    {totalPages > 1 && (
                                                        <Center mt="md">
                                                            <Stack align="center" gap="xs">
                                                                <Text size="sm" c="dimmed">
                                                                    Viser {(activePage - 1) * ITEMS_PER_PAGE + 1}-
                                                                    {Math.min(
                                                                        activePage * ITEMS_PER_PAGE,
                                                                        filteredShifts.length
                                                                    )}{" "}
                                                                    af {filteredShifts.length} vagter
                                                                </Text>
                                                                <Pagination
                                                                    value={activePage}
                                                                    onChange={setActivePage}
                                                                    total={totalPages}
                                                                />
                                                            </Stack>
                                                        </Center>
                                                    )}
                                                </>
                                            )}
                                        </Paper>
                                    </Stack>
                                </Grid.Col>
                                </Grid>

                                <ParticipantsModal
                                    opened={viewOpen}
                                    onClose={() => setViewOpen(false)}
                                    title={viewShiftName}
                                    loading={viewLoading}
                                    data={participants}
                                    onRefresh={refreshAllAfterParticipantChange}
                                />

                            {!readOnly && (
                                <Modal
                                    opened={open}
                                    onClose={() => {
                                        setOpen(false);
                                        setEditShiftId(null);
                                    }}
                                    title={isEditMode ? "Redigér vagt" : "Opret vagt"}
                                    size="lg"
                                >
                                    <Stack>
                                        <Select
                                            label="Funktioner"
                                            data={createFunctionSelectData}
                                            value={formFunctionId ? String(formFunctionId) : null}
                                            onChange={(v) => setFormFunctionId(v ? Number(v) : null)}
                                            required
                                            />


                                        <TextInput
                                            label="Vagt navn"
                                            value={shiftsName}
                                            onChange={(e) => setShiftsName(e.currentTarget.value)}
                                            required
                                        />

                                        <Group grow>
                                            <DateTimePicker
                                                label="Start"
                                                value={startDateTime}
                                                onChange={setStartDateTime}
                                                required
                                            />
                                            <DateTimePicker
                                                label="Slut"
                                                value={endDateTime}
                                                onChange={setEndDateTime}
                                                required
                                                />
                                                <Switch
                                                    label="Slut 1 time efter spil"
                                                    description="Bruger ser ikke klokkeslæt for slut, men teksten '1 time efter spil'"
                                                    checked={endsOneHourAfterPlay}
                                                    onChange={(e) => setEndsOneHourAfterPlay(e.currentTarget.checked)}
                                                />
                                        </Group>

                                        <NumberInput
                                            label="Max deltagere"
                                            min={1}
                                            value={maxParticipants}
                                            onChange={(v) => setMaxParticipants(Number(v || 1))}
                                            required
                                        />

                                        <Switch
                                            label="Venteliste"
                                            checked={waitlist}
                                            onChange={(e) => setWaitlist(e.currentTarget.checked)}
                                        />

                                        <Group justify="flex-end">
                                            <Button
                                                variant="default"
                                                onClick={() => {
                                                    setOpen(false);
                                                    setEditShiftId(null);
                                                }}
                                            >
                                                Annuller
                                            </Button>
                                            <Button loading={saving} onClick={upsertShift}>
                                                Gem
                                            </Button>
                                        </Group>
                                    </Stack>
                                </Modal>
                            )}
                        </>
                    )}
                </Stack>
            </Container>
        </div>
    );
}